/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import * as React from 'react';
import { AnimatePresence } from 'framer-motion';
import styled, { css } from 'styled-components';

import {
  BugIcon,
  CodeCommentIcon,
  EditIcon,
  PlusIcon
} from '../../../icons/Icons';
import { ANIMATIONS } from '../../../utils';
import { PopoverContainer } from '../Popover';

const StyledPopoverContainer = styled(PopoverContainer)`
  position: absolute;
  right: 0;
  top: 100%;
  width: 256px;
  padding: 5px;
  box-shadow:
    0 0 0 1px rgb(11 14 22 / 6%),
    0 3px 6px rgb(11 14 22 / 8%),
    0 5px 10px 0 rgb(11 14 22 / 6%);
`;

const Header = styled.div`
  color: #51388c;
  font-weight: 600;
  font-size: 10px;
  line-height: 13px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 5px 7px;
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
`;

interface MagicPopoverProps {
  isOpen: boolean;
}

export const MagicPopover: React.ComponentType<MagicPopoverProps> = React.memo(
  ({ isOpen }) => {
    return (
      <AnimatePresence>
        {isOpen && (
          <StyledPopoverContainer $position="none" {...ANIMATIONS.Popover}>
            <Header>MAGIC</Header>
            <Items>
              <Item
                description="Insert new code from a prompt"
                icon={<PlusIcon />}
                text="Generate"
              />
              <Item
                description="Change existing code from a prompt"
                icon={<EditIcon />}
                text="Edit"
              />
              <Item
                description="Debug existing code"
                icon={<BugIcon />}
                text="Fix"
              />
              <Item
                description="Add comments explaining your code"
                icon={<CodeCommentIcon />}
                text="Explain"
              />
            </Items>
          </StyledPopoverContainer>
        )}
      </AnimatePresence>
    );
  }
);

const ItemWrapper = styled.div<{ $hovering?: boolean }>`
  display: flex;
  gap: 7px;
  padding: 5px 7px;
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    background-color: rgba(207, 211, 222, 0.15);
  }

  ${({ $hovering }) =>
    $hovering &&
    css`
      background-color: rgba(207, 211, 222, 0.15);
    `}
`;

const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;

  svg {
    color: #717a94;
  }
`;

const ItemInner = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemText = styled.div`
  display: flex;
  align-items: center;
  min-height: 20px;
  color: #01011b;
  font-size: 14px;
`;

const ItemDescription = styled.div`
  margin-top: 1px;
  color: #717a94;
  font-size: 12px;
  line-height: 16px;
`;

interface ItemProps {
  description: string;
  icon: ReactNode;
  text: string;
}

export const Item: React.ComponentType<ItemProps> = React.memo(
  ({ icon, text, description }) => {
    return (
      <ItemWrapper>
        <ItemIcon>{icon}</ItemIcon>
        <ItemInner>
          <ItemText>{text}</ItemText>
          <ItemDescription>{description}</ItemDescription>
        </ItemInner>
      </ItemWrapper>
    );
  }
);
